import { Delete } from "@mui/icons-material";
import { Box, Button, Divider, Grid, IconButton, Paper } from "@mui/material";
import { Fragment, useMemo } from "react";
import ICDField from "./ICDField";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import AddIcon from "@mui/icons-material/Add";
import { overwriteICD10TypeName } from "dto/encounter.dto";
import { useEncounter } from "pages/encounter/encounter.provider";
import { useFieldArray } from "react-hook-form";

interface CoderDiagnosisICDSectionProps {
    name: overwriteICD10TypeName;
    nestedIndex: number;
    swap: (from: number, to: number) => void;
    handleDelete: (index: number) => void;
}

const CoderDiagnosisICDSection = (props: CoderDiagnosisICDSectionProps) => {
    const { name, nestedIndex, swap, handleDelete } = props;

    const { encounterForm, allDiagnosis } = useEncounter();

    const { control, watch } = encounterForm;

    const icdSectionValue = watch(`${name}.${nestedIndex}`);

    const relateDiagnosis = useMemo(() => {
        const existed = allDiagnosis.filter((i) =>
            (icdSectionValue?.diagnosis ?? []).includes(i?.id ?? "")
        );

        return existed;
    }, [icdSectionValue, allDiagnosis]);

    const {
        fields: icdFields,
        append: icdAppend,
        remove: icdRemove,
        swap: icdSwap
    } = useFieldArray({
        name: `${name}.${nestedIndex}.icd`,
        control
    });

    return (
        <Grid container>
            <Grid item xs={11}>
                <Box
                    mb={1}
                    width="100%"
                    component={Paper}
                    variant="outlined"
                    py={2}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <ul>
                                {relateDiagnosis.map((i) => {
                                    return (
                                        <li key={i.id}>
                                            {i.term == "" ? "-" : i.term}
                                        </li>
                                    );
                                })}
                            </ul>
                        </Grid>
                        <Grid item xs={6} display="flex" alignItems="center">
                            <Grid container spacing={1}>
                                {icdFields.map((j, icdIndex) => {
                                    return (
                                        <Fragment key={j.id}>
                                            <Grid item xs={9}>
                                                {name ===
                                                    "overwriteIcd10.principal" &&
                                                icdIndex <= 1 ? (
                                                    <Divider
                                                        sx={{ fontSize: 12 }}
                                                    >
                                                        {icdIndex === 0
                                                            ? "Principal ICD"
                                                            : icdIndex === 1
                                                              ? "Comorbidity ICD"
                                                              : ""}
                                                    </Divider>
                                                ) : null}

                                                <ICDField
                                                    name={name}
                                                    index={nestedIndex}
                                                    icdIndex={icdIndex}
                                                />
                                            </Grid>
                                            <Grid
                                                item
                                                xs={3}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="flex-end"
                                            >
                                                {name !==
                                                    "overwriteIcd10.principal" && (
                                                    <>
                                                        <IconButton
                                                            tabIndex={-1}
                                                            sx={{ padding: 0 }}
                                                            disabled={
                                                                icdIndex === 0
                                                            }
                                                            size="small"
                                                            color="success"
                                                            onClick={() => {
                                                                icdSwap(
                                                                    icdIndex,
                                                                    icdIndex - 1
                                                                );
                                                            }}
                                                        >
                                                            <ArrowUpwardIcon fontSize="inherit" />
                                                        </IconButton>
                                                        <IconButton
                                                            tabIndex={-1}
                                                            sx={{ padding: 0 }}
                                                            disabled={
                                                                icdIndex ===
                                                                icdFields.length -
                                                                    1
                                                            }
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => {
                                                                icdSwap(
                                                                    icdIndex,
                                                                    icdIndex + 1
                                                                );
                                                            }}
                                                        >
                                                            <ArrowDownwardIcon fontSize="inherit" />
                                                        </IconButton>

                                                        <IconButton
                                                            tabIndex={-1}
                                                            onClick={() =>
                                                                icdRemove(
                                                                    icdIndex
                                                                )
                                                            }
                                                            color="error"
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Grid>
                                        </Fragment>
                                    );
                                })}
                                {name !== "overwriteIcd10.principal" && (
                                    <Grid item xs={9}>
                                        <Button
                                            sx={{
                                                borderRadius: 1,
                                                border: "1px dashed var(--green, #05AA97)",
                                                background:
                                                    "rgba(5, 170, 151, 0.05)"
                                            }}
                                            onClick={() => {
                                                icdAppend({ code: "" });
                                            }}
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<AddIcon />}
                                        >
                                            <b>Add More</b>
                                        </Button>
                                    </Grid>
                                )}
                                <Grid item xs={3}>
                                    {" "}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <Grid
                item
                xs={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                    gap: 2,
                    flexDirection: "column"
                }}
            >
                <Box sx={{ display: "flex" }}>
                    <IconButton
                        tabIndex={-1}
                        disabled={
                            nestedIndex === 0 &&
                            name === "overwriteIcd10.principal"
                        }
                        onClick={() => swap(nestedIndex, nestedIndex - 1)}
                        sx={{ padding: 0 }}
                        size="medium"
                        color="success"
                    >
                        <NorthIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                        tabIndex={-1}
                        disabled={
                            nestedIndex + 1 >= 0 &&
                            name === "overwriteIcd10.externalCause"
                        }
                        onClick={() => swap(nestedIndex, nestedIndex + 1)}
                        sx={{ padding: 0 }}
                        size="medium"
                        color="warning"
                    >
                        <SouthIcon fontSize="inherit" />
                    </IconButton>
                </Box>
                {name == "overwriteIcd10.principal" ? null : (
                    <IconButton
                        size="medium"
                        onClick={() => handleDelete(nestedIndex)}
                    >
                        <Delete color="error" />
                    </IconButton>
                )}
            </Grid>
        </Grid>
    );
};

export default CoderDiagnosisICDSection;
